import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import omit from 'lodash/omit';

import { taskFormsApi } from '../../axios';
import { FormTableSortColumn, FormTableSortOrder, TaskForm } from '../../types';
import { getNextSortByNameOrder, type SortByNameOrder } from '../../utils/sortByName';
import type { RootState } from '../store';

interface FormFolderTable {
  // data state for a single folder
  id: number; // 0 for the main table
  name: string;
  taskForms: TaskForm[];
  allFormsIds: number[];
  totalForms: number;
  totalFoundForms: number; // Only used for search
  pendingSignatureForms?: number[];

  // table ui state
  visible: boolean;
  page: number;
  loading: boolean;
  open: boolean;
  selectedForms: number[]; // form ids
  sortOrder: FormTableSortOrder;

  // Request ownership of this folder's list. Holds the id of the request whose
  // answer counts; an earlier one still in flight is stale by definition.
  latestRequestId?: string;

  // What the rows currently on screen were loaded with. The criterion and the
  // page are written before the request leaves, so a failure has to put these
  // back or the header claims an order the rows do not have.
  lastLoadedView?: { sortOrder: FormTableSortOrder; page: number };

  // loaded state
  updated: boolean;
}

interface TaskFormsState {
  formFolders?: {
    [id: number]: FormFolderTable;
  };
  taskForms: TaskForm[];
  activeFormsCount: number;

  loading: boolean;
  formSearchQuery: string;
}

/**
 * Entry state of both paginated tables: the order the service returns, with no
 * sort parameter emitted.
 *
 * A factory and not a shared constant, so two folders never end up pointing at
 * the same object. `column` is inert while `order` is `unsorted`: which column
 * a first click sorts comes from the control that was clicked.
 */
const unsortedFormTableOrder = (): FormTableSortOrder => ({ column: 'name', order: 'unsorted' });

/**
 * The state one column of a folder table is in. A table holds a single
 * criterion, so any column other than the one holding it reads as unsorted.
 *
 * Shared with the header on purpose: it is the one rule that keeps the column
 * ordering the table the only one that can show an order.
 */
export function orderOfColumn(
  sortOrder: FormTableSortOrder,
  column: FormTableSortColumn,
): SortByNameOrder {
  return sortOrder.column === column ? sortOrder.order : 'unsorted';
}

const taskFormsInitialState: TaskFormsState = {
  formFolders: {
    0: {
      id: 0,
      name: 'main-table', // not displayed, useful for debugging
      taskForms: [],
      allFormsIds: [],
      totalForms: 0,
      totalFoundForms: 0,
      pendingSignatureForms: [],

      visible: true,
      page: 0,
      loading: false,
      open: true,
      selectedForms: [],
      sortOrder: unsortedFormTableOrder(),

      updated: false,
    },
  },
  formSearchQuery: '',
  taskForms: [],
  activeFormsCount: 0,
  loading: false,
};

const getAllTaskForms = createAsyncThunk('getAllTaskForms', async (_, { getState }) => {
  const store = getState() as Record<string, TaskFormsState>;
  return await taskFormsApi.getAllTaskForms(store.taskForms.formSearchQuery);
});

// Async Thunks for API Calls:
const getFolders = createAsyncThunk('getFolders', async () => {
  return await taskFormsApi.getTaskFormsFolders();
});

const toggleHideTaskForm = createAsyncThunk(
  'toggleHideTaskForm',
  // folderID ts not used, but it's necessary to be used in the extraReducers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async ({ formId, folderId }: { formId: number; folderId: number }) => {
    return await taskFormsApi.toggleHideTaskForm(formId);
  },
);

const bulkUpdateFormVisibility = createAsyncThunk(
  'bulkUpdateFormVisibility',
  // folderID ts not used, but it's necessary to be used in the extraReducers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async ({ formIds, folderId, hide }: { formIds: number[]; folderId: number; hide: boolean }) => {
    return await taskFormsApi.bulkUpdateFormVisibility(formIds, hide);
  },
);

const cloneTaskForm = createAsyncThunk(
  'cloneTaskForm',
  // folderID ts not used, but it's necessary to be used in the extraReducers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async ({ formId, folderId }: { formId: number; folderId: number }) => {
    return await taskFormsApi.cloneTaskForm(formId);
  },
);

const deleteTaskForm = createAsyncThunk(
  'deleteTaskForm',
  // folderID ts not used, but it's necessary to be used in the extraReducers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async ({ formId, folderId }: { formId: number; folderId: number }) => {
    return await taskFormsApi.deleteTaskForm(formId);
  },
);

const bulkDeleteForms = createAsyncThunk(
  'bulkDeleteForms',
  // folderID ts not used, but it's necessary to be used in the extraReducers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async ({ formIds, folderId }: { formIds: number[]; folderId: number }) => {
    return await taskFormsApi.bulkDeleteForms(formIds);
  },
);

const renameFolder = createAsyncThunk(
  'renameFolder',
  async ({ folderId, name }: { folderId: number; name: string }) => {
    return await taskFormsApi.renameFolder(folderId, name);
  },
);

const createFolder = createAsyncThunk(
  'createFolder',
  async ({ name, formIds }: { name: string; formIds: number[] }) => {
    return await taskFormsApi.createFolder(name, formIds);
  },
);

const deleteFolder = createAsyncThunk(
  'deleteFolder',
  async ({ folderId }: { folderId: number }) => {
    return await taskFormsApi.deleteFolder(folderId);
  },
);

const moveForms = createAsyncThunk(
  'moveForms',
  async ({
    formIds,
    // Its not used, but it's necessary to be used in the extraReducers
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    originId,
    destinationId,
  }: {
    formIds: number[];
    originId: number;
    destinationId: number;
  }) => {
    return await taskFormsApi.moveForms({ formIds, destinationId });
  },
);

const lockEditing = createAsyncThunk(
  'lockEditing',
  // folderID ts not used, but it's necessary to be used in the extraReducers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async ({ formId, folderId }: { formId: number; folderId: number }) => {
    return await taskFormsApi.lockEditing(formId);
  },
);

const unLockEditing = createAsyncThunk(
  'unLockEditing',
  // folderID ts not used, but it's necessary to be used in the extraReducers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async ({ formId, folderId }: { formId: number; folderId: number }) => {
    return await taskFormsApi.unLockEditing(formId);
  },
);

const getFolderForms = createAsyncThunk(
  'getFolderForms',
  async (folderId: number, { getState }) => {
    const store = getState() as Record<string, TaskFormsState>;
    const folder = store.taskForms.formFolders[folderId];
    return await taskFormsApi.getTaskForms({
      // +1 because MUI pagination starts on 0 and backend pagination starts on 1.
      page: folder.page + 1,
      folderId,
      search: store.taskForms.formSearchQuery,
      sortOrder: folder.sortOrder,
    });
  },
  {
    /**
     * The payload creator reads the entry to compose the request, so a folder
     * that is already gone would throw there instead of in a reducer. Not
     * dispatching at all is cheaper than a rejected action nothing acts on.
     */
    condition: (folderId, { getState }) => {
      const store = getState() as Record<string, TaskFormsState>;
      return Object.prototype.hasOwnProperty.call(store.taskForms.formFolders, folderId);
    },
  },
);

const getAllFolderForms = createAsyncThunk('getAllFolderForms', async (folderId: number) => {
  return await taskFormsApi.getAllTaskForms(null, folderId);
});

const searchForms = createAsyncThunk('searchForms', async (_, { getState }) => {
  const store = getState() as Record<string, TaskFormsState>;
  return await taskFormsApi.getFormSearch(1, store.taskForms.formSearchQuery);
});

const getFoldersWithPendingSignatures = createAsyncThunk(
  'getFoldersWithPendingSignatures',
  async () => {
    return await taskFormsApi.getFolderFormsPendingSignatures();
  },
);

const getActiveFormsCount = createAsyncThunk('getActiveFormsCount', async () => {
  return await taskFormsApi.getActiveFormsCount();
});

/**
 * The entry a per-folder handler writes into, or undefined when it is gone.
 *
 * Two things remove an entry while a request of it is in flight: deleting the
 * folder, and leaving the screen, which resets the slice to the single entry of
 * the folderless list. The second one only became reachable when the screen
 * started dispatching `cleanTaskForms`, and a TypeError thrown inside a reducer
 * breaks the store for the rest of the session, so every handler of an async
 * result that resolves an entry by id goes through here. Sync reducers driven
 * by a click (`updateFormFolderSortOrder`, `updateFormFolderPage`) don't: their
 * dispatch is synchronous, so the entry can't vanish between render and reducer.
 */
function folderEntry(state: TaskFormsState, folderId: number) {
  return Object.prototype.hasOwnProperty.call(state.formFolders, folderId)
    ? state.formFolders[folderId]
    : undefined;
}

/**
 * Claims the slot for one folder: from here on this is the request whose answer
 * counts for that folder, and any earlier one still in flight is stale.
 *
 * A token per entry rather than one for the screen, because two folders have
 * requests in flight at the same time and a single token would let the answer
 * of one discard the answer of the other.
 */
function beginFolderFormsRequest(folder: FormFolderTable, requestId: string) {
  folder.latestRequestId = requestId;
  folder.loading = true;
}

/**
 * True when the answer belongs to a request that is no longer the current one
 * of that folder, which includes the case where nothing is current because the
 * screen was left and the state was reset.
 */
function isStaleFolderFormsResponse(folder: FormFolderTable, requestId: string) {
  return folder.latestRequestId !== requestId;
}

// Task Form Slice with reducers:
export const taskFormsSlice = createSlice({
  name: 'taskForms',
  initialState: taskFormsInitialState,
  reducers: {
    cleanTaskForms: () => {
      return { ...taskFormsInitialState };
    },
    updateFormSearchQuery: (state, action: PayloadAction<string>) => {
      state.formSearchQuery = action.payload;
      // Starting a search returns every control to unsorted. The search asks
      // react_form_index for the first page of all folders in one response and
      // that endpoint takes no order, so a criterion left standing would have
      // the header claim Z-A over the unordered page the search returns.
      Object.values(state.formFolders).forEach((folder) => {
        folder.sortOrder = unsortedFormTableOrder();
        // The criterion changed, so any answer still travelling is stale.
        // `loading` has to go with the token: both handlers return early on a
        // stale answer, so leaving it on would disable the controls for good.
        folder.latestRequestId = undefined;
        folder.loading = false;
      });
    },
    clearFormSearch: (state) => {
      state.formSearchQuery = '';
      Object.values(state.formFolders).forEach((folder) => {
        folder.page = 0;
        folder.visible = true;
        folder.open = folder.id === 0;
        folder.updated = false;
        // The page went back to the first one, so the same reasoning as when a
        // search starts: ownership is dropped and the indicator cannot be left
        // on, or the early return of both handlers would keep it there.
        folder.latestRequestId = undefined;
        folder.loading = false;
      });
    },
    /**
     * Cycles the criterion of one folder table: the column it receives advances
     * to its next state and the other column loses its order, because a table
     * holds a single criterion.
     *
     * Cycles from the stored state instead of taking the next value from the
     * control, so the criterion has one owner and two clicks rendered against
     * the same value cannot both resolve to the same next state.
     *
     * Touches the page and the selection of that folder and nothing else. The
     * page goes back to the first one because the rows of the page being looked
     * at are no longer the rows of that position, and the selection goes with
     * it for the same reason. It MUST NOT touch `open`: collapsing the folder
     * being sorted, or any other, is what the PRD's "expandir/contraer" and
     * AC-4 forbid.
     */
    updateFormFolderSortOrder: (
      state,
      action: PayloadAction<{ folderId: number; column: FormTableSortColumn }>,
    ) => {
      const { folderId, column } = action.payload;
      const folder = state.formFolders[folderId];
      const current = orderOfColumn(folder.sortOrder, column);

      folder.sortOrder = { column, order: getNextSortByNameOrder(current) };
      folder.page = 0;
      folder.selectedForms = [];
    },
    updateFormFolderPage: (state, action: PayloadAction<{ folderId?: number; page: number }>) => {
      state.formFolders[action.payload.folderId].page = action.payload.page;
      state.formFolders[action.payload.folderId].selectedForms = [];
    },
    toggleOpenFolder: (state, action: PayloadAction<number>) => {
      state.formFolders[action.payload].open = !state.formFolders[action.payload].open;
    },
    toggleSelectedForm: (state, action: PayloadAction<{ folderId: number; formId: number }>) => {
      const folder = state.formFolders[action.payload.folderId];
      if (folder.selectedForms.includes(action.payload.formId)) {
        folder.selectedForms = folder.selectedForms.filter(
          (formId) => formId !== action.payload.formId,
        );
      } else {
        folder.selectedForms.push(action.payload.formId);
      }
    },
    toggleAllSelectedForms: (state, action: PayloadAction<number>) => {
      const folder = state.formFolders[action.payload];
      if (folder.selectedForms.length === 0) {
        folder.selectedForms = folder.taskForms.map((form) => form.id);
      } else {
        folder.selectedForms = [];
      }
    },
  },
  extraReducers: (builder) => {
    //* Get ALL task forms:
    builder.addCase(getAllTaskForms.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllTaskForms.rejected, (state) => {
      state.taskForms = [];
      state.loading = false;
    });
    builder.addCase(getAllTaskForms.fulfilled, (state, action) => {
      state.taskForms = action.payload.forms;
      state.loading = false;
    });

    //* Get folders:
    builder.addCase(getFolders.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFolders.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getFolders.fulfilled, (state, { payload }) => {
      state.formFolders = payload.reduce((formFolders, folder) => {
        return {
          ...formFolders,
          [folder.id]: {
            id: folder.id,
            name: folder.name,
            taskForms: formFolders[folder.id]?.taskForms ?? [],
            allFormsIds: folder.forms,
            totalForms: folder.total_forms,
            totalFoundForms: formFolders[folder.id]?.totalFoundForms ?? 0,

            visible: formFolders[folder.id]?.visible ?? true,
            page: formFolders[folder.id]?.page ?? 0,
            // Carried too: getFolders() can be dispatched without await while a
            // list request is in flight, so hardcoding false let it win the race
            // and clear the indicator of a folder still waiting.
            loading: formFolders[folder.id]?.loading ?? false,
            open: formFolders[folder.id]?.open ?? false,
            selectedForms: formFolders[folder.id]?.selectedForms ?? [],
            // Carried, not defaulted. This whitelist rebuilds every entry, and
            // moving a form, a bulk delete or the actions menu re-dispatches
            // getFolders(): dropping these would reset the header to unsorted
            // with the rows still ordered by the service, and would hand the
            // token back to its initial value with requests in flight.
            sortOrder: formFolders[folder.id]?.sortOrder ?? unsortedFormTableOrder(),
            latestRequestId: formFolders[folder.id]?.latestRequestId,
            lastLoadedView: formFolders[folder.id]?.lastLoadedView,

            updated: false,
          },
        };
      }, state.formFolders);
      state.loading = false;
    });

    //* Rename folder:
    builder.addCase(renameFolder.pending, (state, { meta }) => {
      const formFolder = folderEntry(state, meta.arg.folderId);
      if (!formFolder) return;
      formFolder.loading = true;
    });
    builder.addCase(renameFolder.rejected, (state, { meta }) => {
      const formFolder = folderEntry(state, meta.arg.folderId);
      if (!formFolder) return;
      formFolder.loading = false;
    });
    builder.addCase(renameFolder.fulfilled, (state, { payload }) => {
      const formFolder = folderEntry(state, payload.id);
      if (!formFolder) return;
      formFolder.name = payload.name;
      formFolder.loading = false;
    });

    //* Create Folder:
    builder.addCase(createFolder.pending, (state) => {
      state.formFolders[0].loading = true;
    });
    builder.addCase(createFolder.rejected, (state) => {
      // The pending handler turns on the indicator of the folderless list, so
      // that is the one to turn off. Writing the screen-wide flag left both of
      // its sort controls disabled for the rest of the session.
      state.formFolders[0].loading = false;
    });
    builder.addCase(createFolder.fulfilled, (state, { payload, meta }) => {
      state.formFolders = {
        ...state.formFolders,
        [payload.id]: {
          id: payload.id,
          name: payload.name,
          taskForms: [],
          totalForms: payload.total_forms,
          allFormsIds: payload.forms,
          totalFoundForms: 0,

          visible: true,
          page: 0,
          loading: false,
          updated: false,
          open: false,
          selectedForms: [],
          // A folder that did not exist a moment ago has no criterion to carry
          // and no request in flight, so the default here is the right answer.
          sortOrder: unsortedFormTableOrder(),
        },
      };
      state.formFolders[0].taskForms = state.formFolders[0].taskForms.filter((form) => {
        return !meta.arg.formIds.includes(form.id);
      });
      state.formFolders[0].loading = false;
      state.formFolders[0].totalForms = state.formFolders[0].taskForms.length;
    });

    //* Delete Folder:
    builder.addCase(deleteFolder.pending, (state, { meta }) => {
      state.formFolders[meta.arg.folderId].loading = true;
      state.formFolders[0].loading = true;
      state.formFolders[0].updated = undefined;
    });
    builder.addCase(deleteFolder.rejected, (state, { meta }) => {
      const formFolder = folderEntry(state, meta.arg.folderId);
      if (formFolder) formFolder.loading = false;
      state.formFolders[0].loading = false;
    });
    builder.addCase(deleteFolder.fulfilled, (state, { meta }) => {
      state.formFolders = omit(state.formFolders, meta.arg.folderId);
      // Turned on by the pending handler of this same request. Nothing turned
      // it off, and once the indicator became functional that left the two sort
      // controls of the folderless list disabled for the rest of the session.
      state.formFolders[0].loading = false;
    });

    //* Toggle visibility:
    builder.addCase(toggleHideTaskForm.pending, (state, { meta }) => {
      const formObject = folderEntry(state, meta.arg.folderId);
      if (!formObject) return;
      formObject.loading = true;
      formObject.updated = undefined;
    });
    builder.addCase(toggleHideTaskForm.rejected, (state, { meta }) => {
      const formObject = folderEntry(state, meta.arg.folderId);
      if (!formObject) return;
      formObject.loading = false;
      formObject.updated = undefined;
    });
    builder.addCase(toggleHideTaskForm.fulfilled, (state, { meta, payload }) => {
      const formObject = folderEntry(state, meta.arg.folderId);
      if (!formObject) return;
      const editedTaskFormIndex = formObject.taskForms.findIndex(
        (taskForm) => taskForm.id === payload.id,
      );
      const newTaskFormsState = [...formObject.taskForms];
      newTaskFormsState[editedTaskFormIndex] = payload;
      formObject.taskForms = newTaskFormsState;
      formObject.loading = false;
      formObject.updated = true;
      if (payload.hidden) state.activeFormsCount--;
      else state.activeFormsCount++;
    });

    //* Bulk Update Visibility:
    builder.addCase(bulkUpdateFormVisibility.pending, (state, { meta }) => {
      const formObject = folderEntry(state, meta.arg.folderId);
      if (!formObject) return;
      formObject.loading = true;
      formObject.updated = undefined;
    });
    builder.addCase(bulkUpdateFormVisibility.rejected, (state, { meta }) => {
      const formObject = folderEntry(state, meta.arg.folderId);
      if (!formObject) return;
      formObject.loading = false;
      formObject.updated = undefined;
    });
    builder.addCase(bulkUpdateFormVisibility.fulfilled, (state, { meta }) => {
      const formTable = folderEntry(state, meta.arg.folderId);
      if (!formTable) return;
      formTable.taskForms = formTable.taskForms.reduce((taskForms, taskForm) => {
        if (meta.arg.formIds.includes(taskForm.id)) {
          return [...taskForms, { ...taskForm, hidden: meta.arg.hide }];
        } else {
          return [...taskForms, taskForm];
        }
      }, []);
      if (meta.arg.hide) state.activeFormsCount -= meta.arg.formIds.length;
      else state.activeFormsCount += meta.arg.formIds.length;
      formTable.selectedForms = [];
      formTable.loading = false;
      formTable.updated = true;
    });

    //* Clone Task Form:
    builder.addCase(cloneTaskForm.pending, (state, { meta }) => {
      const formObject = folderEntry(state, meta.arg.folderId);
      if (!formObject) return;
      formObject.loading = true;
      formObject.updated = undefined;
    });
    builder.addCase(cloneTaskForm.rejected, (state, { meta }) => {
      const formObject = folderEntry(state, meta.arg.folderId);
      if (!formObject) return;
      formObject.loading = false;
      formObject.updated = undefined;
    });
    builder.addCase(cloneTaskForm.fulfilled, (state, { meta, payload }) => {
      const formObject = folderEntry(state, meta.arg.folderId);
      if (!formObject) return;
      const newTaskFormsState = [...formObject.taskForms];
      if (newTaskFormsState.length < 20) {
        newTaskFormsState.push(payload);
        formObject.taskForms = newTaskFormsState;
      }
      formObject.loading = false;
      formObject.updated = true;
      formObject.totalForms++;
      if (state.formSearchQuery) formObject.totalFoundForms++;
      if (!payload.hidden) state.activeFormsCount++;
    });

    //* Delete Task Form:
    builder.addCase(deleteTaskForm.pending, (state, { meta }) => {
      const formFolder = folderEntry(state, meta.arg.folderId);
      if (!formFolder) return;
      formFolder.loading = true;
      formFolder.updated = undefined;
    });
    builder.addCase(deleteTaskForm.rejected, (state, { meta }) => {
      const formFolder = folderEntry(state, meta.arg.folderId);
      if (!formFolder) return;
      formFolder.loading = false;
      formFolder.updated = undefined;
    });
    builder.addCase(deleteTaskForm.fulfilled, (state, { meta, payload }) => {
      const formFolder = folderEntry(state, meta.arg.folderId);
      if (!formFolder) return;
      const filteredTaskForms = [...formFolder.taskForms].filter(
        (taskForm) => taskForm.id !== payload.id,
      );
      formFolder.taskForms = filteredTaskForms;
      formFolder.totalForms--;
      if (!payload.hidden) state.activeFormsCount++;

      // Hide folder if on search and the last form of the folder:
      if (
        formFolder.totalForms > 0 &&
        state.formSearchQuery &&
        formFolder.totalFoundForms === 0 &&
        meta.arg.folderId !== 0
      ) {
        formFolder.visible = false;
      }

      // Check if the page must be changed:
      if (state.formSearchQuery) {
        formFolder.totalFoundForms--;
        if (formFolder.totalFoundForms % 20 === 0 && formFolder.page > 0) formFolder.page--;
      } else {
        if (formFolder.totalForms % 20 === 0 && formFolder.page > 0) formFolder.page--;
      }
    });

    //* Bulk Delete Task Forms:
    builder.addCase(bulkDeleteForms.pending, (state, { meta }) => {
      const formFolder = folderEntry(state, meta.arg.folderId);
      if (!formFolder) return;
      formFolder.loading = true;
      formFolder.updated = undefined;
    });
    builder.addCase(bulkDeleteForms.rejected, (state, { meta }) => {
      const formFolder = folderEntry(state, meta.arg.folderId);
      if (!formFolder) return;
      formFolder.loading = false;
      formFolder.updated = undefined;
    });
    builder.addCase(bulkDeleteForms.fulfilled, (state, { meta }) => {
      const formFolder = folderEntry(state, meta.arg.folderId);
      if (!formFolder) return;
      // handle possible pagination change
      formFolder.totalForms -= meta.arg.formIds.length;
      const deletedActiveForms = formFolder.taskForms.filter(
        (form) => meta.arg.formIds.includes(form.id) && !form.hidden,
      );
      state.activeFormsCount -= deletedActiveForms.length;
      // Change page if it's the last form in the page (page > 0):
      if (state.formSearchQuery) {
        formFolder.totalFoundForms -= meta.arg.formIds.length;
        if (formFolder.totalFoundForms % 20 === 0 && formFolder.page > 0) formFolder.page--;
      } else {
        if (formFolder.totalForms % 20 === 0 && formFolder.page > 0) formFolder.page--;
      }
      formFolder.selectedForms = [];
    });

    //* Move Task Forms:
    builder.addCase(moveForms.pending, (state, { meta }) => {
      const originTable = folderEntry(state, meta.arg.originId);
      const destinationTable = folderEntry(state, meta.arg.destinationId);
      if (!originTable || !destinationTable) return;
      originTable.loading = true;
      originTable.updated = undefined;
      destinationTable.loading = true;
      destinationTable.updated = undefined;
    });
    builder.addCase(moveForms.rejected, (state, { meta }) => {
      const originTable = folderEntry(state, meta.arg.originId);
      const destinationTable = folderEntry(state, meta.arg.destinationId);
      if (originTable) originTable.loading = false;
      if (destinationTable) destinationTable.loading = false;
    });
    builder.addCase(moveForms.fulfilled, (state, { meta }) => {
      const originTable = folderEntry(state, meta.arg.originId);
      const destinationTable = folderEntry(state, meta.arg.destinationId);
      if (!originTable || !destinationTable) return;

      // handle possible pagination change
      originTable.totalForms -= meta.arg.formIds.length;
      // Change page if it's the last form in the page (page > 0):
      if (state.formSearchQuery) {
        originTable.totalFoundForms -= meta.arg.formIds.length;
        if (originTable.totalFoundForms % 20 === 0 && originTable.page > 0) originTable.page--;
      } else {
        if (originTable.totalForms % 20 === 0 && originTable.page > 0) originTable.page--;
      }

      destinationTable.totalForms += meta.arg.formIds.length;
      if (state.formSearchQuery) destinationTable.totalFoundForms += meta.arg.formIds.length;

      originTable.updated = true;
      destinationTable.updated = true;
      // Turned on by the pending handler of this same request and never off.
      originTable.loading = false;
      destinationTable.loading = false;
    });

    //* Get Folder Forms:
    builder.addCase(getFolderForms.pending, (state, { meta }) => {
      if (!Object.prototype.hasOwnProperty.call(state.formFolders, meta.arg)) return; // Folder was deleted
      beginFolderFormsRequest(state.formFolders[meta.arg], meta.requestId);
    });
    builder.addCase(getFolderForms.rejected, (state, { meta }) => {
      if (!Object.prototype.hasOwnProperty.call(state.formFolders, meta.arg)) return; // Folder was deleted
      const formFolder = state.formFolders[meta.arg];
      // A superseded failure may not clear the indicator of the request that
      // replaced it and is still travelling.
      if (isStaleFolderFormsResponse(formFolder, meta.requestId)) return;
      // The criterion and the page were written before the request left. A
      // failure that reverts nothing leaves the header showing the order that
      // was clicked over the rows of the previous one.
      if (formFolder.lastLoadedView) {
        formFolder.sortOrder = { ...formFolder.lastLoadedView.sortOrder };
        formFolder.page = formFolder.lastLoadedView.page;
      }
      formFolder.loading = false;
    });
    builder.addCase(getFolderForms.fulfilled, (state, { meta, payload }) => {
      // The handler that reads the token, so it needs the guard its siblings
      // already had: without it the read throws on undefined when the folder
      // was deleted while its request was travelling.
      if (!Object.prototype.hasOwnProperty.call(state.formFolders, meta.arg)) return; // Folder was deleted
      const formFolder = state.formFolders[meta.arg];
      // Cuts the whole handler before any field is written. Narrowed to the row
      // assignment, a superseded answer would still write `open` and could
      // collapse a folder the user is looking at.
      if (isStaleFolderFormsResponse(formFolder, meta.requestId)) return;

      formFolder.taskForms = payload.forms;
      if (state.formSearchQuery) {
        formFolder.totalFoundForms = payload.total_forms;
        formFolder.visible = payload.total_forms > 0;
      } else {
        formFolder.totalForms = payload.total_forms;
      }
      // Second writer of `open` on the path of a sort change, and it runs after
      // the sort reducer. It may only decide `open` for a folder that never
      // loaded, which is the first-expand flow that depends on it
      // (formFolders/folder.tsx dispatches this thunk instead of toggling).
      // Keyed on `updated` and not on `open`: an answer that arrives after the
      // user collapsed the folder would reopen it.
      if (!formFolder.updated) formFolder.open = payload.total_forms > 0;
      formFolder.selectedForms = [];
      formFolder.loading = false;
      formFolder.updated = true;
      formFolder.lastLoadedView = { sortOrder: { ...formFolder.sortOrder }, page: formFolder.page };
    });

    //* Get All Folder Forms:
    builder.addCase(getAllFolderForms.pending, (state, { meta }) => {
      const formFolder = folderEntry(state, meta.arg);
      if (!formFolder) return;
      formFolder.loading = true;
    });
    builder.addCase(getAllFolderForms.rejected, (state, { meta }) => {
      const formFolder = folderEntry(state, meta.arg);
      if (!formFolder) return;
      formFolder.loading = false;
    });
    builder.addCase(getAllFolderForms.fulfilled, (state, { meta, payload }) => {
      // No request token here, unlike its paginated sibling: this thunk is never
      // dispatched from a screen that also has the forms table mounted, so the
      // two cannot race for the same view.
      const formFolder = folderEntry(state, meta.arg);
      if (!formFolder) return;
      formFolder.taskForms = payload.forms;
      formFolder.totalForms = payload.total_forms;
      formFolder.loading = false;
      formFolder.updated = true;
    });

    //* Search Forms:
    builder.addCase(searchForms.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(searchForms.rejected, (state) => {
      state.loading = false;
      // The term reducer already returned every control to unsorted and the
      // rows never changed, so the whole screen would claim an order it is not
      // showing.
      Object.values(state.formFolders).forEach((folder) => {
        if (folder.lastLoadedView) folder.sortOrder = { ...folder.lastLoadedView.sortOrder };
      });
    });
    builder.addCase(searchForms.fulfilled, (state, { payload }) => {
      const foldersWithFoundForms = Object.keys(payload).map((folderId) => parseInt(folderId));

      // hide folders with no found forms:
      state.formFolders = Object.values(state.formFolders).reduce((formFolders, folder) => {
        return {
          ...formFolders,
          [folder.id]: {
            ...folder,
            visible: foldersWithFoundForms.includes(folder.id) || folder.id === 0,
          },
        };
      }, state.formFolders);

      // reset main table forms and pagination:
      state.formFolders[0].taskForms = [];
      state.formFolders[0].page = 0;
      state.formFolders[0].totalFoundForms = 0;

      // replace the forms in the folders with the found forms:
      state.formFolders = foldersWithFoundForms.reduce((formFolders, folderId) => {
        return {
          ...formFolders,
          [folderId]: {
            ...formFolders[folderId],
            taskForms: payload[folderId].forms,
            totalFoundForms: payload[folderId].total_forms,
            page: 0,
            open: false,

            updated: true,
          },
        };
      }, state.formFolders);

      state.loading = false;
    });

    //* Get Folders with Pending Signatures:
    builder.addCase(getFoldersWithPendingSignatures.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFoldersWithPendingSignatures.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getFoldersWithPendingSignatures.fulfilled, (state, { payload }) => {
      state.formFolders = Object.values(state.formFolders).reduce((formFolders, folder) => {
        return {
          ...formFolders,
          [folder.id]: {
            ...folder,
            pendingSignatureForms: payload[folder.id],
          },
        };
      }, state.formFolders);
      state.loading = false;
    });

    //* Get Active Forms Count:
    builder.addCase(getActiveFormsCount.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getActiveFormsCount.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getActiveFormsCount.fulfilled, (state, { payload }) => {
      state.activeFormsCount = payload.active_forms_count;
    });

    //* Lock Editing:
    builder.addCase(lockEditing.pending, (state, { meta }) => {
      const folderId = meta.arg.folderId || 0;
      const formObject = state.formFolders[folderId];
      if (formObject) {
        formObject.loading = true;
        formObject.updated = undefined;
      }
    });
    builder.addCase(lockEditing.rejected, (state, { meta }) => {
      const folderId = meta.arg.folderId || 0;
      const formObject = state.formFolders[folderId];
      if (formObject) {
        formObject.loading = false;
        formObject.updated = undefined;
      }
    });
    builder.addCase(lockEditing.fulfilled, (state, { meta, payload }) => {
      const folderId = meta.arg.folderId || 0;
      const formObject = state.formFolders[folderId];
      if (!formObject) return;

      const editedTaskFormIndex = formObject.taskForms.findIndex(
        (taskForm) => taskForm.id === payload.id,
      );
      if (editedTaskFormIndex === -1) return;

      const newTaskFormsState = [...formObject.taskForms];
      newTaskFormsState[editedTaskFormIndex] = {
        ...newTaskFormsState[editedTaskFormIndex],
        ...payload,
      };
      formObject.taskForms = newTaskFormsState;
      formObject.loading = false;
      formObject.updated = true;
    });

    //* Unlock Editing:
    builder.addCase(unLockEditing.pending, (state, { meta }) => {
      const folderId = meta.arg.folderId || 0;
      const formObject = state.formFolders[folderId];
      if (formObject) {
        formObject.loading = true;
        formObject.updated = undefined;
      }
    });
    builder.addCase(unLockEditing.rejected, (state, { meta }) => {
      const folderId = meta.arg.folderId || 0;
      const formObject = state.formFolders[folderId];
      if (formObject) {
        formObject.loading = false;
        formObject.updated = undefined;
      }
    });
    builder.addCase(unLockEditing.fulfilled, (state, { meta, payload }) => {
      const folderId = meta.arg.folderId || 0;
      const formObject = state.formFolders[folderId];
      if (!formObject) return;

      const editedTaskFormIndex = formObject.taskForms.findIndex(
        (taskForm) => taskForm.id === payload.id,
      );
      if (editedTaskFormIndex === -1) return;

      const newTaskFormsState = [...formObject.taskForms];
      newTaskFormsState[editedTaskFormIndex] = {
        ...newTaskFormsState[editedTaskFormIndex],
        ...payload,
      };
      formObject.taskForms = newTaskFormsState;
      formObject.loading = false;
      formObject.updated = true;
    });
  },
});

export const {
  cleanTaskForms,
  updateFormSearchQuery,
  clearFormSearch,
  updateFormFolderPage,
  updateFormFolderSortOrder,
  toggleOpenFolder,
  toggleSelectedForm,
  toggleAllSelectedForms,
} = taskFormsSlice.actions;
export {
  getAllTaskForms,
  getFolders,
  toggleHideTaskForm,
  bulkUpdateFormVisibility,
  cloneTaskForm,
  deleteTaskForm,
  bulkDeleteForms,
  renameFolder,
  createFolder,
  deleteFolder,
  getFolderForms,
  getAllFolderForms,
  searchForms,
  moveForms,
  lockEditing,
  unLockEditing,
  getFoldersWithPendingSignatures,
  getActiveFormsCount,
};

//* Custom selectors:
// Select only visible folders:
export function selectFilteredFolders(state: RootState) {
  return Object.values(state.taskForms.formFolders).filter((folder) => folder.id && folder.visible);
}

// Interface to resemble previous "folders" state:
export function selectFolders(state: RootState) {
  return Object.values(state.taskForms.formFolders)
    .filter((folder) => folder.id !== 0)
    .map((folder) => {
      return {
        ...folder,
        forms: folder.allFormsIds,
        enabledForms: folder.allFormsIds.filter(
          (formId) => !state.taskForms.taskForms.find((form) => form.id === formId)?.hidden,
        ),
      };
    });
}

export function selectFolder(state: RootState, folderId: number) {
  return state.taskForms.formFolders[folderId];
}

export default taskFormsSlice.reducer;
